import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Droplet, 
  AlertCircle, 
  Clock, 
  TrendingDown, 
  Flame, 
  Activity, 
  Heart,
  ChevronRight,
  AlertTriangle,
  Stethoscope,
  FileText,
  LinkIcon,
  Zap
} from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import UnifiedSearch from '../components/UnifiedSearch';
import LanguageToggle from '../components/LanguageToggle';
import { symptomsData } from '../data/symptomsContent';
import { t } from '../utils/translations';

// Icon mapping
const iconMap = {
  'droplet': Droplet,
  'alert-circle': AlertCircle,
  'clock': Clock,
  'trending-down': TrendingDown,
  'flame': Flame,
  'activity': Activity,
  'heart': Heart,
  'zap': Zap
};

// Spanish translations for symptom titles and summaries
const symptomTranslations = {
  'blood-in-urine': {
    title: 'Sangre en la Orina (Hematuria)',
    summary: 'Entender las causas de orina roja, rosa o marrón y cuándo es una emergencia.'
  },
  'frequent-urination': {
    title: 'Micción Frecuente',
    summary: 'Causas de ir al baño con demasiada frecuencia, de día o de noche.'
  },
  'painful-urination': {
    title: 'Micción Dolorosa (Disuria)',
    summary: 'Por qué la micción puede quemar o doler, y qué condiciones considerar.'
  },
  'weak-urine-stream': {
    title: 'Flujo de Orina Débil',
    summary: 'Causas de flujo urinario reducido y dificultad para vaciar la vejiga.'
  },
  'urinary-urgency': {
    title: 'Urgencia Urinaria',
    summary: 'Entender las urgencias repentinas y fuertes de orinar.'
  },
  'testicular-pain': {
    title: 'Dolor Testicular',
    summary: 'Causas de dolor o malestar escrotal y cuándo buscar atención.'
  },
  'pelvic-pain': {
    title: 'Dolor Pélvico',
    summary: 'Dolor pélvico crónico o agudo en hombres puede originarse de la próstata, vejiga u otras causas.'
  },
  'erectile-problems': {
    title: 'Problemas de Erección',
    summary: 'Comprender por qué ocurren los problemas de erección y las opciones de tratamiento.'
  },
  'flank-pain': {
    title: 'Dolor en el Costado',
    summary: 'Causas de dolor en el costado o la espalda relacionadas con los riñones.'
  },
  'urinary-leakage': {
    title: 'Pérdida de Orina (Incontinencia)',
    summary: 'Comprender los tipos de incontinencia urinaria y las opciones de tratamiento efectivas.'
  }
};

// UI text translations
const uiText = {
  en: {
    symptomGuide: 'Symptom Guide',
    symptomGuideDesc: 'Learn what common urological symptoms mean, when to seek care, and what conditions they may indicate.',
    allSymptoms: 'All Symptoms',
    whatItMeans: 'What This Symptom Means',
    commonCauses: 'Common Causes',
    whenSerious: 'When It May Be Serious',
    typicalTests: 'Typical Tests Your Doctor May Order',
    relatedConditions: 'Related Conditions',
    relatedConditionsIntro: 'Learn more about conditions associated with this symptom:',
    learnMore: 'Learn more',
    notFound: 'Symptom Not Found',
    notFoundDesc: "The symptom page you're looking for doesn't exist.",
    backToSymptoms: 'Back to Symptoms',
    disclaimer: 'This content is for educational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment. Always consult your physician or a qualified healthcare provider with questions regarding a medical condition.',
    importantNote: 'If you\'re experiencing symptoms, please consult a healthcare provider for proper evaluation and diagnosis.'
  },
  es: {
    symptomGuide: 'Guía de Síntomas',
    symptomGuideDesc: 'Aprenda qué significan los síntomas urológicos comunes, cuándo buscar atención y qué condiciones pueden indicar.',
    allSymptoms: 'Todos los Síntomas',
    whatItMeans: 'Qué Significa Este Síntoma',
    commonCauses: 'Causas Comunes',
    whenSerious: 'Cuándo Puede Ser Grave',
    typicalTests: 'Pruebas Típicas Que Su Médico Puede Ordenar',
    relatedConditions: 'Condiciones Relacionadas',
    relatedConditionsIntro: 'Aprenda más sobre las condiciones asociadas con este síntoma:',
    learnMore: 'Más información',
    notFound: 'Síntoma No Encontrado',
    notFoundDesc: 'La página de síntomas que busca no existe.',
    backToSymptoms: 'Volver a Síntomas',
    disclaimer: 'Este contenido es solo para fines educativos y no sustituye el consejo, diagnóstico o tratamiento médico profesional. Siempre consulte a su médico o proveedor de salud calificado con preguntas sobre una condición médica.',
    importantNote: 'Si está experimentando síntomas, consulte a un proveedor de atención médica para una evaluación y diagnóstico adecuados.'
  }
};

// Symptoms Index Page
export const SymptomsIndexPage = () => {
  const [language, setLanguage] = useState('en');
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  const getText = (key) => uiText[language][key];

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: language === 'en' ? 'Home' : 'Inicio', path: '/' },
        { label: language === 'en' ? 'Symptoms' : 'Síntomas', path: '/symptoms' }
      ]} />

      <div className="page-header-centered" data-testid="symptoms-index">
        <div className="page-header-top">
          <h1>{getText('symptomGuide')}</h1>
          <LanguageToggle language={language} onToggle={toggleLanguage} />
        </div>
        <p>{getText('symptomGuideDesc')}</p>
      </div>

      {/* Search Bar */}
      <div className="hub-search-wrapper">
        <UnifiedSearch placeholder={language === 'en' ? 'Search symptoms, conditions, treatments...' : 'Buscar síntomas, condiciones, tratamientos...'} variant="hub" />
      </div>

      <div className="symptoms-grid" data-testid="symptoms-grid">
        {symptomsData.map(symptom => {
          const IconComponent = iconMap[symptom.icon] || AlertCircle;
          const translation = symptomTranslations[symptom.id];
          return (
            <Link 
              to={`/symptoms/${symptom.id}`}
              key={symptom.id}
              className="symptom-card"
              data-testid={`symptom-card-${symptom.id}`}
            >
              <div className="symptom-card-icon">
                <IconComponent size={28} />
              </div>
              <div className="symptom-card-content">
                <h3>{language === 'es' && translation ? translation.title : symptom.title}</h3>
                <p>{language === 'es' && translation ? translation.summary : symptom.summary}</p>
              </div>
              <ChevronRight size={20} className="symptom-card-arrow" />
            </Link>
          );
        })}
      </div>

      <div className="symptoms-disclaimer">
        <AlertTriangle size={20} />
        <p>
          <strong>{language === 'en' ? 'Important:' : 'Importante:'}</strong> {getText('importantNote')}
        </p>
      </div>
    </PageWrapper>
  );
};

// Individual Symptom Page
const SymptomPage = () => {
  const { symptomId } = useParams();
  const [language, setLanguage] = useState('en');
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [symptomId]);

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  const getText = (key) => uiText[language][key];

  const symptom = symptomsData.find(s => s.id === symptomId);

  if (!symptom) {
    return (
      <PageWrapper>
        <div className="empty-state">
          <h2>{getText('notFound')}</h2>
          <p>{getText('notFoundDesc')}</p>
          <Link to="/symptoms" className="btn-primary">
            {getText('backToSymptoms')}
          </Link>
        </div>
      </PageWrapper>
    );
  }

  const IconComponent = iconMap[symptom.icon] || AlertCircle;
  const { content } = symptom;
  const translation = symptomTranslations[symptomId];

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: language === 'en' ? 'Home' : 'Inicio', path: '/' },
        { label: language === 'en' ? 'Symptoms' : 'Síntomas', path: '/symptoms' },
        { label: language === 'es' && translation ? translation.title : symptom.title, path: `/symptoms/${symptom.id}` }
      ]} />

      <div className="symptom-page" data-testid={`symptom-page-${symptom.id}`}>
        {/* Header */}
        <div className="symptom-header">
          <div className="symptom-header-nav">
            <Link to="/symptoms" className="back-link">
              <ArrowLeft size={20} />
              <span>{getText('allSymptoms')}</span>
            </Link>
            <LanguageToggle language={language} onToggle={toggleLanguage} />
          </div>
          
          <div className="symptom-title-section">
            <div className="symptom-icon-large">
              <IconComponent size={36} />
            </div>
            <h1>{language === 'es' && translation ? translation.title : symptom.title}</h1>
          </div>
          <p className="symptom-summary">{language === 'es' && translation ? translation.summary : symptom.summary}</p>
        </div>

        {/* What It Means */}
        <section className="symptom-section" data-testid="section-what-it-means">
          <div className="section-header">
            <FileText size={22} />
            <h2>{getText('whatItMeans')}</h2>
          </div>
          <div className="section-content markdown-content">
            {content.whatItMeans.split('\n\n').map((paragraph, idx) => (
              <p key={idx} dangerouslySetInnerHTML={{ 
                __html: paragraph
                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n/g, '<br />')
              }} />
            ))}
          </div>
        </section>

        {/* Common Causes */}
        <section className="symptom-section" data-testid="section-common-causes">
          <div className="section-header">
            <Stethoscope size={22} />
            <h2>{getText('commonCauses')}</h2>
          </div>
          <div className="causes-list">
            {content.commonCauses.map((cause, idx) => (
              <div key={idx} className="cause-item">
                <div className="cause-info">
                  <h4>{cause.condition}</h4>
                  <p>{cause.description}</p>
                </div>
                {cause.articleId && (
                  <Link 
                    to={`/learn/${cause.articleId}`} 
                    className="cause-link"
                    data-testid={`cause-link-${idx}`}
                  >
                    {getText('learnMore')}
                    <ChevronRight size={16} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* When It May Be Serious */}
        <section className="symptom-section warning-section" data-testid="section-when-serious">
          <div className="section-header">
            <AlertTriangle size={22} />
            <h2>{getText('whenSerious')}</h2>
          </div>
          <div className="section-content markdown-content warning-content">
            {content.whenSerious.split('\n\n').map((paragraph, idx) => (
              <div key={idx} dangerouslySetInnerHTML={{ 
                __html: paragraph
                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n- /g, '</li><li>')
                  .replace(/^- /, '<ul><li>')
                  .replace(/<\/li>$/, '</li></ul>')
                  .replace(/\n/g, '<br />')
              }} />
            ))}
          </div>
        </section>

        {/* Typical Tests */}
        <section className="symptom-section" data-testid="section-typical-tests">
          <div className="section-header">
            <Activity size={22} />
            <h2>{getText('typicalTests')}</h2>
          </div>
          <div className="tests-grid">
            {content.typicalTests.map((test, idx) => (
              <div key={idx} className="test-item">
                <h4>{test.test}</h4>
                <p>{test.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Related Conditions */}
        <section className="symptom-section" data-testid="section-related-conditions">
          <div className="section-header">
            <LinkIcon size={22} />
            <h2>{getText('relatedConditions')}</h2>
          </div>
          <p className="section-intro">{getText('relatedConditionsIntro')}</p>
          <div className="related-conditions-list">
            {content.relatedConditions.map((condition, idx) => (
              <Link 
                key={idx}
                to={`/learn/${condition.articleId}`}
                className="related-condition-link"
                data-testid={`related-condition-${idx}`}
              >
                <span>{condition.title}</span>
                <ChevronRight size={18} />
              </Link>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <div className="symptom-disclaimer">
          <AlertTriangle size={18} />
          <p>
            <strong>{language === 'en' ? 'Disclaimer:' : 'Aviso:'}</strong> {getText('disclaimer')}
          </p>
        </div>
      </div>
    </PageWrapper>
  );
};

export default SymptomPage;
